import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { LiffProvider } from './providers/LiffProvider';
import { ThemeProvider } from './contexts/ThemeContext';
import { CartProvider } from './contexts/CartContext';
import { FeatureProvider } from './contexts/FeatureContext';
import { useMobile } from './hooks/useMobile';
import { useMobileViewportFix } from './hooks/useMobileViewportFix';
import { useFeatures } from './hooks/useFeatures';
import { useAuth } from './contexts/AuthContext';
import HomePage from './pages/HomePage';
import AdsListPage from './pages/advertising/AdsListPage';
import AdDetailPage from './pages/advertising/AdDetailPage';
import AdFormPage from './pages/advertising/AdFormPage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import EmailVerificationPage from './pages/auth/EmailVerificationPage';
import VerifyEmailLinkPage from './pages/auth/VerifyEmailLinkPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import UnsubscribePage from './pages/UnsubscribePage';
import MemberPage from './pages/member/MemberDashboardPage';
import MemberDashboardPage from './pages/member/MemberDashboardPage';
import WishListPage from './pages/wishes/WishListPage';
import WishDetailPage from './pages/wishes/WishDetailPage';
import WishFormPage from './pages/wishes/WishFormPage';
import ProductsListPage from './pages/products/ProductsListPage';
import ProductDetailPage from './pages/products/ProductDetailPage';
import CartPage from './pages/cart/CartPage';
import CheckoutPage from './pages/checkout/CheckoutPage';
import CheckoutSuccessPage from './pages/checkout/CheckoutSuccessPage';
import OrdersListPage from './pages/orders/OrdersListPage';
import OrderDetailPage from './pages/orders/OrderDetailPage';
import HelpPage from './pages/help/HelpPage';
import MobileHomePage from './pages/mobile/MobileHomePage';
import MobileAdsListPage from './pages/mobile/MobileAdsListPage';
import MobileAdDetailPage from './pages/mobile/MobileAdDetailPage';
import MobileAdFormPage from './pages/mobile/MobileAdFormPage';
import MobileWishListPage from './pages/mobile/MobileWishListPage';
import MobileWishDetailPage from './pages/mobile/MobileWishDetailPage';
import MobileWishFormPage from './pages/mobile/MobileWishFormPage';
import MobileProductsListPage from './pages/mobile/MobileProductsListPage';
import MobileProductDetailPage from './pages/mobile/MobileProductDetailPage';
import MobileCartPage from './pages/mobile/cart/MobileCartPage';
import MobileCheckoutPage from './pages/mobile/checkout/MobileCheckoutPage';
import MobileCheckoutSuccessPage from './pages/mobile/checkout/MobileCheckoutSuccessPage';
import MobileOrdersListPage from './pages/mobile/orders/MobileOrdersListPage';
import MobileOrderDetailPage from './pages/mobile/orders/MobileOrderDetailPage';
import MobileHelpPage from './pages/mobile/MobileHelpPage';
import MobileMemberDashboardPage from './pages/mobile/MobileMemberDashboardPage';
import ThemeVersionPage from './pages/admin/theme/version';
import ThemePreviewPage from './pages/admin/theme/preview/[token]';
import SystemBootstrapPage from './pages/admin/system/bootstrap';
import AdminAnalyticsDashboard from './pages/admin/analytics/AdminAnalyticsDashboard';
import AdminProductsListPage from './pages/admin/products/AdminProductsListPage';
import AdminProductFormPage from './pages/admin/products/AdminProductFormPage';
import AdminOrdersListPage from './pages/admin/orders/AdminOrdersListPage';
import AdminOrderDetailPage from './pages/admin/orders/AdminOrderDetailPage';
import AdminFeaturesPage from './pages/admin/features/AdminFeaturesPage';
import { FeatureDisabledPage } from './pages/features/FeatureDisabledPage';
import { FeatureGuard } from './components/feature-guard/FeatureGuard';
import ProtectedRoute from './components/auth/ProtectedRoute';
import PublicRoute from './components/auth/PublicRoute';
import AdminRoute from './components/auth/AdminRoute';
import LiffApp from './components/line/LiffApp';
import LiffHomePage from './pages/line/LiffHomePage';
import LiffProductPage from './pages/line/LiffProductPage';
import MaintenancePage from './pages/MaintenancePage';
import MobileMaintenancePage from './pages/mobile/MobileMaintenancePage';
import NotFoundPage from './pages/NotFoundPage';
import MobileNotFoundPage from './pages/mobile/MobileNotFoundPage';
import './App.css';

// Wrapper component to handle dynamic device-based routing
const DeviceAwareRoute: React.FC<{
  desktopComponent: React.ComponentType;
  mobileComponent: React.ComponentType;
}> = ({ desktopComponent: DesktopComponent, mobileComponent: MobileComponent }) => {
  const { isPhone, isTablet } = useMobile();
  const shouldUseMobileView = isPhone || isTablet;
  const Component = shouldUseMobileView ? MobileComponent : DesktopComponent;
  return <Component />;
};

function App() {
  // Fix mobile viewport issues - applies to all mobile pages
  useMobileViewportFix();

  return (
    <LiffProvider>
      <AuthProvider>
        <ThemeProvider>
          <CartProvider>
            <FeatureProvider>
              <AppContent />
            </FeatureProvider>
          </CartProvider>
        </ThemeProvider>
      </AuthProvider>
    </LiffProvider>
  );
}

function AppContent() {
  // Get feature flags - must be inside FeatureProvider
  const features = useFeatures();

  // Get user auth context for admin role check
  const { user } = useAuth();

  // Check if maintenance mode is enabled
  const isMaintenanceMode = features.isMaintenanceMode && features.config?.maintenance?.enabled;

  // Check if user is admin
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  // Check if admin access is allowed
  const allowAdminAccess = features.config?.maintenance?.allowAdminAccess !== false;

  // If maintenance mode is enabled and user is not admin (or admin access is not allowed), show maintenance page
  if (isMaintenanceMode && (!isAdmin || !allowAdminAccess)) {
    return (
      <Router>
        <Routes>
          <Route
            path="*"
            element={<DeviceAwareRoute desktopComponent={MaintenancePage} mobileComponent={MobileMaintenancePage} />}
          />
        </Routes>
      </Router>
    );
  }

  return (
    <Router>
      <Routes>
        {/* Routes with dynamic device detection */}
        <Route
          path="/"
          element={<DeviceAwareRoute desktopComponent={HomePage} mobileComponent={MobileHomePage} />}
        />
        <Route
          path="/ads"
          element={
            <FeatureGuard featureEnabled={features.isAdsEnabled} loading={features.loading}>
              <DeviceAwareRoute desktopComponent={AdsListPage} mobileComponent={MobileAdsListPage} />
            </FeatureGuard>
          }
        />
        <Route
          path="/ads/:adId"
          element={
            <FeatureGuard featureEnabled={features.isAdsEnabled} loading={features.loading}>
              <DeviceAwareRoute desktopComponent={AdDetailPage} mobileComponent={MobileAdDetailPage} />
            </FeatureGuard>
          }
        />
        <Route
          path="/ads/new"
          element={
            <FeatureGuard featureEnabled={features.isAdsEnabled} loading={features.loading}>
              <ProtectedRoute>
                <DeviceAwareRoute desktopComponent={AdFormPage} mobileComponent={MobileAdFormPage} />
              </ProtectedRoute>
            </FeatureGuard>
          }
        />
        <Route
          path="/ads/:adId/edit"
          element={
            <FeatureGuard featureEnabled={features.isAdsEnabled} loading={features.loading}>
              <ProtectedRoute>
                <DeviceAwareRoute desktopComponent={AdFormPage} mobileComponent={MobileAdFormPage} />
              </ProtectedRoute>
            </FeatureGuard>
          }
        />

        {/* Authentication Routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <DeviceAwareRoute desktopComponent={LoginPage} mobileComponent={LoginPage} />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <DeviceAwareRoute desktopComponent={RegisterPage} mobileComponent={RegisterPage} />
            </PublicRoute>
          }
        />
        <Route
          path="/verify-email"
          element={<EmailVerificationPage />}
        />
        <Route
          path="/verify-email-link"
          element={<VerifyEmailLinkPage />}
        />
        <Route
          path="/forgot-password"
          element={<ForgotPasswordPage />}
        />
        <Route
          path="/reset-password"
          element={<ResetPasswordPage />}
        />
        <Route
          path="/unsubscribe"
          element={<UnsubscribePage />}
        />

        {/* Wishes Routes */}
        <Route
          path="/wishes"
          element={
            <FeatureGuard featureEnabled={features.isWishesEnabled} loading={features.loading}>
              <DeviceAwareRoute desktopComponent={WishListPage} mobileComponent={MobileWishListPage} />
            </FeatureGuard>
          }
        />
        <Route
          path="/wishes/:id"
          element={
            <FeatureGuard featureEnabled={features.isWishesEnabled} loading={features.loading}>
              <DeviceAwareRoute desktopComponent={WishDetailPage} mobileComponent={MobileWishDetailPage} />
            </FeatureGuard>
          }
        />
        <Route
          path="/wishes/new"
          element={
            <FeatureGuard featureEnabled={features.isWishesEnabled} loading={features.loading}>
              <ProtectedRoute>
                <DeviceAwareRoute desktopComponent={WishFormPage} mobileComponent={MobileWishFormPage} />
              </ProtectedRoute>
            </FeatureGuard>
          }
        />

        {/* Products Routes */}
        <Route
          path="/products"
          element={
            <FeatureGuard featureEnabled={features.isProductsEnabled} loading={features.loading}>
              <DeviceAwareRoute desktopComponent={ProductsListPage} mobileComponent={MobileProductsListPage} />
            </FeatureGuard>
          }
        />
        <Route
          path="/products/:id"
          element={
            <FeatureGuard featureEnabled={features.isProductsEnabled} loading={features.loading}>
              <DeviceAwareRoute desktopComponent={ProductDetailPage} mobileComponent={MobileProductDetailPage} />
            </FeatureGuard>
          }
        />
        <Route
          path="/good/:id"
          element={
            <FeatureGuard featureEnabled={features.isProductsEnabled} loading={features.loading}>
              <LiffProductPage />
            </FeatureGuard>
          }
        />

        {/* Cart Routes */}
        <Route
          path="/cart"
          element={
            <FeatureGuard featureEnabled={features.isCartEnabled} loading={features.loading}>
              <ProtectedRoute>
                <DeviceAwareRoute desktopComponent={CartPage} mobileComponent={MobileCartPage} />
              </ProtectedRoute>
            </FeatureGuard>
          }
        />

        {/* Checkout Routes */}
        <Route
          path="/checkout"
          element={
            <FeatureGuard featureEnabled={features.isCheckoutEnabled} loading={features.loading}>
              <ProtectedRoute>
                <DeviceAwareRoute desktopComponent={CheckoutPage} mobileComponent={MobileCheckoutPage} />
              </ProtectedRoute>
            </FeatureGuard>
          }
        />
        <Route
          path="/checkout/success"
          element={
            <FeatureGuard featureEnabled={features.isCheckoutEnabled} loading={features.loading}>
              <ProtectedRoute>
                <DeviceAwareRoute desktopComponent={CheckoutSuccessPage} mobileComponent={MobileCheckoutSuccessPage} />
              </ProtectedRoute>
            </FeatureGuard>
          }
        />

        {/* Orders Routes */}
        <Route
          path="/orders"
          element={
            <FeatureGuard featureEnabled={features.isOrdersEnabled} loading={features.loading}>
              <ProtectedRoute>
                <DeviceAwareRoute desktopComponent={OrdersListPage} mobileComponent={MobileOrdersListPage} />
              </ProtectedRoute>
            </FeatureGuard>
          }
        />
        <Route
          path="/orders/:id"
          element={
            <FeatureGuard featureEnabled={features.isOrdersEnabled} loading={features.loading}>
              <ProtectedRoute>
                <DeviceAwareRoute desktopComponent={OrderDetailPage} mobileComponent={MobileOrderDetailPage} />
              </ProtectedRoute>
            </FeatureGuard>
          }
        />

        {/* Member Routes */}
        <Route
          path="/member"
          element={
            <ProtectedRoute>
              <DeviceAwareRoute desktopComponent={MemberPage} mobileComponent={MobileMemberDashboardPage} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/member/dashboard"
          element={
            <ProtectedRoute>
              <DeviceAwareRoute desktopComponent={MemberDashboardPage} mobileComponent={MemberPage} />
            </ProtectedRoute>
          }
        />

        {/* Admin Routes */}
        <Route
          path="/admin"
          element={
            <AdminRoute allowedRoles={['admin', 'super_admin']}>
              <DeviceAwareRoute desktopComponent={AdminAnalyticsDashboard} mobileComponent={MobileHelpPage} />
            </AdminRoute>
          }
        />
        {/* Temporarily using AdminAnalyticsDashboard as placeholder for unimplemented admin pages */}
        {/* <Route
          path="/admin/members"
          element={
            <AdminRoute allowedRoles={['admin', 'super_admin']}>
              <DeviceAwareRoute desktopComponent={AdminMembersListPage} mobileComponent={MobileHelpPage} />
            </AdminRoute>
          }
        /> */}
        {/* <Route
          path="/admin/ads"
          element={
            <AdminRoute allowedRoles={['admin', 'super_admin']}>
              <DeviceAwareRoute desktopComponent={AdminAdsListPage} mobileComponent={MobileHelpPage} />
            </AdminRoute>
          }
        /> */}
        {/* <Route
          path="/admin/ads/:adId"
          element={
            <AdminRoute allowedRoles={['admin', 'super_admin']}>
              <DeviceAwareRoute desktopComponent={AdminAdDetailPage} mobileComponent={MobileHelpPage} />
            </AdminRoute>
          }
        /> */}
        {/* <Route
          path="/admin/wishes"
          element={
            <AdminRoute allowedRoles={['admin', 'super_admin']}>
              <DeviceAwareRoute desktopComponent={AdminWishesListPage} mobileComponent={MobileHelpPage} />
            </AdminRoute>
          }
        /> */}
        {/* <Route
          path="/admin/wishes/:wishId"
          element={
            <AdminRoute allowedRoles={['admin', 'super_admin']}>
              <DeviceAwareRoute desktopComponent={AdminWishDetailPage} mobileComponent={MobileHelpPage} />
            </AdminRoute>
          }
        /> */}
        <Route
          path="/admin/products"
          element={
            <AdminRoute allowedRoles={['admin', 'super_admin']}>
              <DeviceAwareRoute desktopComponent={AdminProductsListPage} mobileComponent={MobileHelpPage} />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/products/new"
          element={
            <AdminRoute allowedRoles={['admin', 'super_admin']}>
              <DeviceAwareRoute desktopComponent={AdminProductFormPage} mobileComponent={MobileHelpPage} />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/products/:id/edit"
          element={
            <AdminRoute allowedRoles={['admin', 'super_admin']}>
              <DeviceAwareRoute desktopComponent={AdminProductFormPage} mobileComponent={MobileHelpPage} />
            </AdminRoute>
          }
        />
        {/* <Route
          path="/admin/products/:id"
          element={
            <AdminRoute allowedRoles={['admin', 'super_admin']}>
              <DeviceAwareRoute desktopComponent={AdminProductDetailPage} mobileComponent={MobileHelpPage} />
            </AdminRoute>
          }
        /> */}
        <Route
          path="/admin/orders"
          element={
            <AdminRoute allowedRoles={['admin', 'super_admin']}>
              <DeviceAwareRoute desktopComponent={AdminOrdersListPage} mobileComponent={MobileHelpPage} />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/orders/:id"
          element={
            <AdminRoute allowedRoles={['admin', 'super_admin']}>
              <DeviceAwareRoute desktopComponent={AdminOrderDetailPage} mobileComponent={MobileHelpPage} />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/theme/version"
          element={
            <AdminRoute allowedRoles={['super_admin']}>
              <DeviceAwareRoute desktopComponent={ThemeVersionPage} mobileComponent={MobileHelpPage} />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/theme/preview/:token"
          element={
            <AdminRoute allowedRoles={['super_admin']}>
              <DeviceAwareRoute desktopComponent={ThemePreviewPage} mobileComponent={MobileHelpPage} />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/system/bootstrap"
          element={
            <AdminRoute allowedRoles={['super_admin']}>
              <DeviceAwareRoute desktopComponent={SystemBootstrapPage} mobileComponent={MobileHelpPage} />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/features"
          element={
            <AdminRoute allowedRoles={['super_admin']}>
              <AdminFeaturesPage />
            </AdminRoute>
          }
        />

        {/* LIFF Routes */}
        <Route path="/liff" element={<LiffApp />}>
          <Route index element={<LiffHomePage />} />
          <Route path="home" element={<LiffHomePage />} />
        </Route>

        {/* Help Routes */}
        <Route
          path="/help"
          element={
            <ProtectedRoute>
              <DeviceAwareRoute desktopComponent={HelpPage} mobileComponent={MobileHelpPage} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/help/:manualId"
          element={
            <ProtectedRoute>
              <DeviceAwareRoute desktopComponent={HelpPage} mobileComponent={MobileHelpPage} />
            </ProtectedRoute>
          }
        />

        {/* Feature Disabled Route */}
        <Route
          path="/feature-disabled"
          element={<FeatureDisabledPage featureName="此功能" />}
        />

        {/* 404 Not Found - Must be last route */}
        <Route
          path="*"
          element={<DeviceAwareRoute desktopComponent={NotFoundPage} mobileComponent={MobileNotFoundPage} />}
        />
      </Routes>
    </Router>
  );
}

export default App;
